import { ReleaseInfo } from '@/generated/ReleaseInfo';
import { getSessionCorrelationId } from '@/littledash';
import InVivoError from '@/model/InVivoError.ts';
import { datadogRum } from '@datadog/browser-rum';
import { UserApiId } from '@/model/User.model';
import { TeamApiId } from '@/model/Team.model';
import { datadogLogs } from '@datadog/browser-logs';

type StatusType = 'ok' | 'debug' | 'info' | 'notice' | 'warn' | 'error' | 'critical' | 'alert' | 'emerg';
export class DatadogUtils {
  static #site = 'datadoghq.com';
  static #service = 'in-vivo.web';

  static init() {
    if (AppConfig.datadogEnabled) {
      datadogRum.init({
        applicationId: AppConfig.datadogRumApplicationId as string,
        clientToken: AppConfig.datadogRumClientToken as string,
        site: DatadogUtils.#site,
        service: DatadogUtils.#service,
        useSecureSessionCookie: true,
        env: `${AppConfig.environment}`,
        version: ReleaseInfo.releaseVersion,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 100,
        trackResources: true,
        trackLongTasks: true,
        trackUserInteractions: true,
        defaultPrivacyLevel: 'mask-user-input',
        allowedTracingUrls: [AppConfig.allowedHostPattern],
      });
      datadogRum.setGlobalContextProperty('session_correlation', { id: getSessionCorrelationId() });
      datadogRum.setGlobalContextProperty('Commit', {
        commitId: ReleaseInfo.commitId,
        commitTime: ReleaseInfo.commitTime,
      });
      datadogRum.startSessionReplayRecording();

      if (AppConfig.datadogLogsEnabled) {
        datadogLogs.init({
          clientToken: AppConfig.datadogRumClientToken as string,
          site: DatadogUtils.#site,
          service: DatadogUtils.#service,
          env: `${AppConfig.environment}`,
          version: ReleaseInfo.releaseVersion,
          forwardErrorsToLogs: true,
          sessionSampleRate: 100,
        });
        datadogLogs.setGlobalContextProperty('session_correlation', { id: getSessionCorrelationId() });
        datadogLogs.setGlobalContextProperty('Commit', {
          commitId: ReleaseInfo.commitId,
          commitTime: ReleaseInfo.commitTime,
        });
      }
    }
  }

  static setUser(userApiId: UserApiId, email: string, teamApiId: TeamApiId, teamName: string, teamSubdomain?: string) {
    if (AppConfig.datadogEnabled) {
      datadogRum.setUser({ id: `${userApiId}`, email });
      datadogRum.setGlobalContextProperty('tenant', teamSubdomain ?? teamName);
      datadogRum.setGlobalContextProperty('tenant_id', teamApiId);
      datadogRum.setGlobalContextProperty('session_correlation', { id: getSessionCorrelationId() });
      if (AppConfig.datadogLogsEnabled) {
        datadogLogs.setUser({ id: `${userApiId}`, email });
        datadogLogs.setGlobalContextProperty('tenant', teamSubdomain ?? teamName);
        datadogLogs.setGlobalContextProperty('tenant_id', teamApiId);
        datadogLogs.setGlobalContextProperty('session_correlation', { id: getSessionCorrelationId() });
      }
    }
  }

  /**
   *
   * @param exception {unknown}
   * @param context {Record<string,any>|undefined}
   */
  static captureException(exception: unknown, context?: Record<string, any>) {
    if (AppConfig.datadogEnabled) {
      const appErrorContext = Object.entries(exception instanceof InVivoError ? exception.context : {}).reduce(
        (acc, [key, value]) => ({ ...acc, [`InVivoError: ${key}`]: value }),
        {}
      );
      datadogRum.addError(exception, {
        'Current URL': window.location.href,
        Online: navigator?.onLine ?? false,
        Focused: document.hasFocus(),
        'Visibility State': document.visibilityState,
        'Round Trip Time': `${navigator?.connection?.rtt ?? '-1'}ms`,
        ...appErrorContext,
        ...(context ?? {}),
      });
    } else {
      console.error('Datadog.captureException()', exception); // eslint-disable-line no-console
    }
  }
  static captureAction(name: string, context?: Record<string, any>) {
    if (AppConfig.datadogEnabled) {
      datadogRum.addAction(name, context);
    }
  }

  static log(message: string, messageContext?: Record<string, any>, status: StatusType = 'info', error?: Error) {
    if (AppConfig.datadogEnabled && AppConfig.datadogLogsEnabled) {
      datadogLogs.logger.log(message, messageContext, status, error);
    }
  }
}
